import { FC } from "react"
import { Button as CustomButton } from "components"
import { DropZone, SelectedFilePreview } from "components/new-stuff/file-upload"
import { useFileUpload } from "hooks/useFileUpload"
import ColumnTag from "./ColumnTag"
import StepActionBar from "./StepActionBar"
import { UploadTimesheetProps } from "./types"
import { DEFAULT_OPTIONAL_COLUMNS, DEFAULT_REQUIRED_COLUMNS } from "./constants"

const TIMESHEET_ALLOWED_EXTENSIONS = ["csv", "xls", "xlsx"]

const UploadTimesheet: FC<UploadTimesheetProps> = ({
  selectedFile,
  onFileChange,
  onParse,
  requiredColumns = DEFAULT_REQUIRED_COLUMNS,
  optionalColumns = DEFAULT_OPTIONAL_COLUMNS,
  acceptedFormats = "CSV, XLS, or XLSX",
  maxFileSizeMB = 50,
  handleNextStep,
}) => {
  const { isProcessing, processFile } = useFileUpload({
    onFileChange,
    onParse,
    allowedExtensions: TIMESHEET_ALLOWED_EXTENSIONS,
    maxFileSizeMB,
    acceptedFormatsLabel: acceptedFormats,
  })

  return (
    <div className="w-full">
      <p className="text-sm">
        For accurate mapping please ensure your csv includes these columns.
      </p>
      <div className="flex flex-wrap items-center text-sm gap-2 mt-2">
        <span className="font-semibold text-slate-900 mr-1">
          Required fields
        </span>
        {requiredColumns.map(({ title }) => (
          <ColumnTag
            key={title}
            title={title}
          />
        ))}
      </div>
      {optionalColumns.length > 0 && (
        <div className="flex flex-wrap items-center text-sm gap-2 mt-2 mb-4">
          <span className="font-semibold text-slate-900 mr-1">
            Optional fields
          </span>
          {optionalColumns.map(({ title }) => (
            <ColumnTag
              key={title}
              title={title}
            />
          ))}
        </div>
      )}

      <DropZone
        onFileSelected={processFile}
        isProcessing={isProcessing}
        allowedExtensions={TIMESHEET_ALLOWED_EXTENSIONS}
        acceptedFormatsLabel={acceptedFormats}
        maxFileSizeMB={maxFileSizeMB}
      />

      {selectedFile && (
        <SelectedFilePreview
          key={`${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`}
          file={selectedFile}
          isProcessing={isProcessing}
          onDelete={() => onFileChange(null)}
        />
      )}

      <div className="pb-24" />

      <StepActionBar>
        <CustomButton
          color="primary"
          className="font-semibold"
          disabled={!selectedFile || isProcessing}
          submitting={isProcessing}
          onClick={handleNextStep}
        >
          Next
        </CustomButton>
      </StepActionBar>
    </div>
  )
}

export default UploadTimesheet
